<template>
  <ion-content>
    <ion-list>
      <ion-list-header>{{ translate("More options") }}</ion-list-header>
      <ion-item @click="viewJobHistory(job)" button>
        <ion-icon slot="start" :icon="timeOutline" />
        {{ translate("History") }}
      </ion-item>
      <ion-item @click="copyJobInformation(job)" button>
        <ion-icon slot="start" :icon="copyOutline" />
        {{ translate("Copy details") }}
      </ion-item>
      <ion-item @click="updatePinnedJobs(job?.systemJobEnumId)" button>
        <ion-icon slot="start" :icon="pinOutline" />
        {{ translate("Pin job") }}
      </ion-item>
      <ion-item :disabled="!hasPermission(Actions.APP_JOB_UPDATE)" @click="openJobCustomParameterModal()" lines="none" button>
        <ion-icon slot="start" :icon="flashOutline" />
        {{ translate("Run now") }}
      </ion-item>      
    </ion-list>
  </ion-content>
</template>

<script lang="ts">
import {
  IonContent,
  IonIcon,
  IonItem,
  IonList,
  IonListHeader,
  alertController,
  modalController,
  popoverController
} from "@ionic/vue";
import { defineComponent } from "vue";
import { copyOutline, flashOutline, pinOutline, timeOutline  } from 'ionicons/icons'
import { mapGetters, useStore } from 'vuex'
import JobHistoryModal from '@/components/JobHistoryModal.vue'
import { Plugins } from '@capacitor/core';
import { generateJobCustomOptions, showToast } from '@/utils'
import emitter from "@/event-bus"
import { Actions, hasPermission } from '@/authorization';
import { translate } from "@hotwax/dxp-components";
import JobParameterModal from '@/components/JobParameterModal.vue'

export default defineComponent({
  name: "JobActionsPopover",
  components: { 
    IonContent,
    IonIcon,
    IonItem,
    IonList,
    IonListHeader
  },
  props: ["job"],
  computed: {
    ...mapGetters({
      getPinnedJobs: 'user/getPinnedJobs'
    })
  },
  methods: {
    closePopover() {
      popoverController.dismiss({ dismissed: true });
    },
    async copyJobInformation(job: any) {
      const { Clipboard } = Plugins;
      const jobDetails = `jobId: ${job.jobId}, jobName: ${job.enumName}, jobDescription: ${job.description} ${job.runtimeData ? (", runtimeData: " + JSON.stringify(job.runtimeData)) : ""}`;

      await Clipboard.write({
        string: jobDetails
      }).then(() => {
        showToast(translate("Copied job details to clipboard"));
      })
      this.closePopover();
    },
    async viewJobHistory(job: any) {
      const jobHistoryModal = await modalController.create({
        component: JobHistoryModal,
        componentProps: { currentJob: job }
      });
      await jobHistoryModal.present();
      jobHistoryModal.onDidDismiss().then(() => {
        this.closePopover();
      })
    },
    async updatePinnedJobs(enumId: any) {
      const pinnedJobs = new Set(this.getPinnedJobs);
      if(pinnedJobs.has(enumId)) {
        pinnedJobs.delete(enumId);
        await this.store.dispatch('user/updatePinnedJobs', { pinnedJobs: [...pinnedJobs] });
        emitter.emit("pinnedJobsUpdated", enumId);
      } else {
        pinnedJobs.add(enumId);
        await this.store.dispatch('user/updatePinnedJobs', { pinnedJobs: [...pinnedJobs] });
      }
      this.closePopover();
    },
    async openJobCustomParameterModal() {
      const jobParameterModal = await modalController.create({
        component: JobParameterModal,
        componentProps: {
          customOptionalParameters: generateJobCustomOptions(this.job).optionalParameters,
          customRequiredParameters: generateJobCustomOptions(this.job).requiredParameters,
          currentJob: JSON.parse(JSON.stringify(this.job)),
          runNow: true
        },
        breakpoints: [0, 0.25, 0.5, 0.75, 1],
        initialBreakpoint: 1
      });
      await jobParameterModal.present();
      this.closePopover();
    },
  },
  setup() {
    const store = useStore();  

    return {
      Actions,
      copyOutline, 
      flashOutline,
      hasPermission,
      pinOutline,
      store, 
      timeOutline,
      translate
    }
  }
});
</script> 